import { useCallback, useEffect, useMemo, useState } from "react";
import type { CanvasesCanvasRun, SuperplaneComponentsNode as ComponentsNode } from "@/api-client";
import { getHeaderIconSrc } from "@/ui/componentSidebar/integrationIconMaps";
import {
  buildNodeMap,
  buildRunPresentation,
  statusFiltersToApiFilters,
  type RunStatusFilter,
} from "@/ui/Runs/runPresentation";
import { runMatchesStatusTriggerFilters } from "@/ui/Runs/runStatusTriggerFilter";
import type { CanvasRunsFilters } from "@/hooks/useCanvasData";
import { loadPersistedFilters, savePersistedFilters } from "./filterPersistence";
import type { TriggerOption } from "./RunFiltersPopover";

interface UseRunFiltersParams {
  runs: CanvasesCanvasRun[];
  workflowNodes: ComponentsNode[];
  componentIconMap: Record<string, string>;
  onFiltersChange?: (filters: CanvasRunsFilters) => void;
}

export function useRunFilters({ runs, workflowNodes, componentIconMap, onFiltersChange }: UseRunFiltersParams) {
  const [selectedTriggerIds, setSelectedTriggerIds] = useState<Set<string>>(() => loadPersistedFilters().triggerIds);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<RunStatusFilter>>(() => loadPersistedFilters().statuses);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const nodeMap = useMemo(() => buildNodeMap(workflowNodes), [workflowNodes]);

  const triggerOptions = useMemo<TriggerOption[]>(
    () =>
      workflowNodes
        .filter((node) => node.id && node.type === "TYPE_TRIGGER")
        .map((node) => ({
          id: node.id!,
          name: node.name || node.component || "Trigger",
          iconSrc: getHeaderIconSrc(node.component),
          iconSlug: node.component ? componentIconMap[node.component] : undefined,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [workflowNodes, componentIconMap],
  );

  useEffect(() => {
    savePersistedFilters({ statuses: selectedStatuses, triggerIds: selectedTriggerIds });
  }, [selectedStatuses, selectedTriggerIds]);

  useEffect(() => {
    if (triggerOptions.length === 0) return;
    const validTriggerIds = new Set(triggerOptions.map((option) => option.id));
    setSelectedTriggerIds((currentTriggerIds) => {
      const nextTriggerIds = new Set(Array.from(currentTriggerIds).filter((id) => validTriggerIds.has(id)));
      return nextTriggerIds.size === currentTriggerIds.size ? currentTriggerIds : nextTriggerIds;
    });
  }, [triggerOptions]);

  useEffect(() => {
    onFiltersChange?.({
      ...statusFiltersToApiFilters(Array.from(selectedStatuses)),
      triggerIds: Array.from(selectedTriggerIds).sort(),
      search: debouncedSearch,
    });
  }, [selectedStatuses, selectedTriggerIds, debouncedSearch, onFiltersChange]);

  const decoratedRuns = useMemo(() => runs.map((run) => buildRunPresentation(run, nodeMap)), [runs, nodeMap]);

  const statusTriggerFilters = useMemo(
    () => ({
      statuses: selectedStatuses.size > 0 ? Array.from(selectedStatuses) : undefined,
      triggers: selectedTriggerIds.size > 0 ? Array.from(selectedTriggerIds) : undefined,
    }),
    [selectedStatuses, selectedTriggerIds],
  );

  const filteredRuns = useMemo(() => {
    if (onFiltersChange) return decoratedRuns;
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    return decoratedRuns.filter(({ run, haystack }) => {
      // Shared matcher with console run datasources — keeps status/trigger
      // semantics identical across the sidebar and widget surfaces.
      if (!runMatchesStatusTriggerFilters(run, statusTriggerFilters)) return false;
      if (normalizedSearchQuery && !haystack.includes(normalizedSearchQuery)) return false;
      return true;
    });
  }, [decoratedRuns, searchQuery, statusTriggerFilters, onFiltersChange]);

  const orderedRuns = useMemo(
    () => ({
      active: filteredRuns.filter((run) => run.status === "running"),
      rest: filteredRuns.filter((run) => run.status !== "running"),
    }),
    [filteredRuns],
  );

  const hasSearchFilter = searchQuery.trim().length > 0;
  const hasAnyFilter = selectedTriggerIds.size > 0 || selectedStatuses.size > 0 || hasSearchFilter;

  const clearFilters = useCallback(() => {
    setSelectedStatuses(new Set());
    setSelectedTriggerIds(new Set());
    setSearchQuery("");
  }, []);

  const toggleStatus = useCallback((status: RunStatusFilter) => {
    setSelectedStatuses((currentStatuses) => {
      const nextStatuses = new Set(currentStatuses);
      if (nextStatuses.has(status)) nextStatuses.delete(status);
      else nextStatuses.add(status);
      return nextStatuses;
    });
  }, []);

  const toggleTrigger = useCallback((triggerId: string) => {
    setSelectedTriggerIds((currentTriggerIds) => {
      const nextTriggerIds = new Set(currentTriggerIds);
      if (nextTriggerIds.has(triggerId)) nextTriggerIds.delete(triggerId);
      else nextTriggerIds.add(triggerId);
      return nextTriggerIds;
    });
  }, []);

  return {
    selectedStatuses,
    selectedTriggerIds,
    searchQuery,
    triggerOptions,
    filteredRuns,
    orderedRuns,
    hasSearchFilter,
    hasAnyFilter,
    setSearchQuery,
    clearFilters,
    toggleStatus,
    toggleTrigger,
    clearStatuses: () => setSelectedStatuses(new Set()),
    clearTriggers: () => setSelectedTriggerIds(new Set()),
  };
}

export type RunFiltersState = ReturnType<typeof useRunFilters>;
